import { useState } from "react";
import { toast } from "sonner";
import { Check, Inbox, Loader2, Trash2, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, errText } from "@/lib/api";
import { fmtDateTime } from "@/lib/time";
import { useAdmin } from "@/context/AdminContext";
import { TypeTag } from "./CasesHub";
import { caseField, caseLabel } from "./OpenCaseDialog";

const SOURCE = { public: "Public complaint form", email_report: "\"This wasn't me\" email report" };

const OpenFromReport = ({ report, meta, onClose, onOpened }) => {
  const { user } = useAdmin();
  const [handler, setHandler] = useState(user?.id || "");
  const [suspend, setSuspend] = useState(false);
  const [busy, setBusy] = useState(false);
  const handlers = (meta?.handlers || []).filter((h) => h.id !== report?.subject_id || h.owner);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/cases/reports/${report.id}/open`, { handler_id: handler, suspend });
      toast.success(`Case ${data.ref} opened`); onOpened(data);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!report} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none border-t-4 border-t-[#E62314] bg-[#FBF9F5] sm:max-w-lg" data-testid="report-open-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">Open a case file</DialogTitle>
          <DialogDescription>A case reference is created now and emailed to {report?.complainant?.email || report?.subject_email || "the person who reported it"}.</DialogDescription>
        </DialogHeader>
        <label><span className={caseLabel}>Handled by</span>
          <select value={handler} onChange={(e) => setHandler(e.target.value)} className={caseField} data-testid="report-open-handler">
            {handlers.map((h) => <option key={h.id} value={h.id}>{h.name}{h.id === user?.id ? " (me)" : ""}{h.job_title ? ` · ${h.job_title}` : ""}</option>)}
          </select></label>
        {report?.subject_id && (
          <label className="flex items-start justify-between gap-3 border border-[#C21B0E]/40 bg-white p-3 text-sm font-semibold">
            <span>Also suspend the account through this case<span className="mt-0.5 block text-xs font-normal text-[#57534E]">They can't log in until you lift it or close the case.</span></span>
            <Switch checked={suspend} onCheckedChange={setSuspend} data-testid="report-open-suspend" />
          </label>
        )}
        <button onClick={submit} disabled={busy || !handler} className="flex w-full items-center justify-center gap-2 bg-[#0A0A0A] py-3.5 text-sm font-bold text-white hover:bg-[#E62314] disabled:opacity-40" data-testid="report-open-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Open case file</button>
      </DialogContent>
    </Dialog>
  );
};

const ReportRow = ({ r, onOpen, onDecline, onRemove }) => (
  <li className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-start sm:justify-between" data-testid={`report-${r.id}`}>
    <div className="min-w-0 flex-1">
      <span className="flex flex-wrap items-center gap-1.5"><TypeTag c={r} /><span className="text-[11px] font-bold uppercase tracking-wider text-[#57534E]">{SOURCE[r.source] || "Report"}</span></span>
      <p className="mt-1 break-words text-sm font-semibold">{r.title}</p>
      <p className="mt-1 whitespace-pre-wrap break-words text-sm text-[#3a3836]" data-testid={`report-${r.id}-summary`}>{r.summary}</p>
      <p className="mt-1 text-xs text-[#57534E]">Received {fmtDateTime(r.created_at)}{r.complainant?.email ? ` · from ${r.complainant.name} (${r.complainant.email})` : ""}</p>
      {r.status === "opened" && <p className="mt-1 text-xs font-bold text-[#15803D]" data-testid={`report-${r.id}-opened`}>Case opened · {r.case_ref}</p>}
      {r.status === "declined" && <p className="mt-1 text-xs font-bold text-[#C21B0E]" data-testid={`report-${r.id}-declined`}>Not opened{r.reason ? ` · ${r.reason}` : ""}</p>}
    </div>
    <div className="flex shrink-0 flex-wrap gap-2">
      {r.status === "waiting" ? <>
        <button onClick={() => onOpen(r)} className="flex items-center gap-1.5 bg-[#16A34A] px-3 py-1.5 text-xs font-bold text-white" data-testid={`report-${r.id}-open`}><Check className="h-3.5 w-3.5" />Open case</button>
        <button onClick={() => onDecline(r)} className="flex items-center gap-1.5 bg-[#E62314] px-3 py-1.5 text-xs font-bold text-white" data-testid={`report-${r.id}-decline`}><X className="h-3.5 w-3.5" />Don't open</button>
      </> : <button onClick={() => onRemove(r)} aria-label="Remove from list" className="flex h-8 w-8 items-center justify-center text-[#57534E] hover:bg-black/10 hover:text-[#E62314]" data-testid={`report-${r.id}-remove`}><Trash2 className="h-4 w-4" /></button>}
    </div>
  </li>
);

export const ReportsQueue = ({ reports, meta, onChanged, onCaseOpened }) => {
  const [opening, setOpening] = useState(null);
  const [showDone, setShowDone] = useState(false);
  const decline = async (r) => {
    const reason = window.prompt("Why isn't a case being opened? (optional, emailed to them)");
    if (reason === null) return;
    try { await api.post(`/cases/reports/${r.id}/decline`, { reason }); toast.success("Report closed without a case"); onChanged(); } catch (e) { toast.error(errText(e)); }
  };
  const remove = async (r) => {
    try { await api.delete(`/cases/reports/${r.id}`); onChanged(); } catch (e) { toast.error(errText(e)); }
  };
  const list = Array.isArray(reports) ? reports : [];
  const waiting = list.filter((r) => r.status === "waiting");
  const done = list.filter((r) => r.status !== "waiting");
  const rows = (items) => <ul className="divide-y divide-black/10">{items.map((r) => <ReportRow key={r.id} r={r} onOpen={setOpening} onDecline={decline} onRemove={remove} />)}</ul>;
  return (
    <section className="mt-5 border border-[#0A0A0A]/80 bg-white" data-testid="reports-queue">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#0A0A0A]/80 bg-[#0A0A0A] px-4 py-2 text-white">
        <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em]"><Inbox className="h-4 w-4" />Waiting to be opened</p>
        {done.length > 0 && <button onClick={() => setShowDone((s) => !s)} className="text-xs font-semibold text-zinc-300 hover:text-white" data-testid="reports-toggle-done">{showDone ? "Hide dealt with" : `Show dealt with (${done.length})`}</button>}
      </div>
      {!waiting.length ? <p className="p-6 text-center text-sm text-[#57534E]" data-testid="reports-none">No reports waiting. New complaints and "this wasn't me" reports appear here first.</p> : rows(waiting)}
      {showDone && done.length > 0 && <div className="border-t-2 border-black/10 bg-[#FAFAF9]">{rows(done)}</div>}
      <OpenFromReport report={opening} meta={meta} onClose={() => setOpening(null)} onOpened={(c) => { setOpening(null); onChanged(); onCaseOpened(c); }} />
    </section>
  );
};
